import { Input, MenuProps } from 'antd';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  injectIntl,
  defineMessages,
  IntlShape,
  FormattedMessage,
} from 'react-intl';
import { PlusCircleTwoTone } from '@ant-design/icons';

import Connector, { PropsFromRedux } from './Connector';
import { OperatorFormWrapper, StyledDropdown, StyledTable } from './style';
import {
  getEmrTableData,
  getIntegratedEMRsColumns,
  EmrTableDataType,
} from './utils';
import EditableCell from 'src/components/CrudCommonComponents/EditableCell';
import EditableRow from 'src/components/CrudCommonComponents/EditableRow';
import { UUID } from 'src/types/utility';
import { ExtraActionBtn } from '../../style';
import { MODAL_MODES } from 'src/utils/constants';

type Props = PropsFromRedux & {
  intl: IntlShape;
  customerId: UUID;
  modalMode: keyof typeof MODAL_MODES | '';
  setDisabledNextBtn: Dispatch<SetStateAction<boolean>>;
};

const OperatorForm = ({
  intl,
  customerId,
  handleChangeForm,
  editableData,
  emrApiProviders,
  modalMode,
  createOperator,
  editOperator,
  isFormOperatorLoading,
  setDisabledNextBtn,
}: Props) => {
  const EditableContext = React.createContext(null);
  const columns = getIntegratedEMRsColumns(intl);

  const [tableData, setTableData] = useState<EmrTableDataType[]>([]);

  useEffect(() => {
    if (editableData?.emrMappings?.length && emrApiProviders) {
      const emrTableData = getEmrTableData(editableData, emrApiProviders);
      setTableData(emrTableData);
    }
  }, [editableData, customerId]);

  const updateCell = (_: unknown, organizationId: UUID) => {
    handleChangeForm({
      ...editableData,
      emrMappings: editableData?.emrMappings?.map(mapping => ({
        ...mapping,
        organizationId,
      })),
    });
    setDisabledNextBtn(true);
  };

  const items: MenuProps['items'] = emrApiProviders?.map(apiProvider => ({
    key: apiProvider.id,
    label: apiProvider.name,
    onClick: () => {
      setTableData([
        {
          apiProviderName: apiProvider.name,
          organizationId: '',
          integrationType: 'Full',
        },
      ]);
      handleChangeForm({
        ...editableData,
        emrMappings: [
          {
            apiProviderId: apiProvider.id,
            organizationId: '',
            emrType: apiProvider.emrType,
          },
        ],
      });
    },
  }));

  const tableEditableComponents = {
    body: {
      row: (props: unknown) => (
        // @ts-ignore createContext type
        <EditableRow EditableContext={EditableContext} {...props} />
      ),
      cell: (props: unknown) => (
        <EditableCell
          EditableContext={EditableContext}
          updateElement={updateCell}
          cellName="organizationId"
          isRequired={true}
          // TODO: for now organization id should't be validate by pattern
          // validatePattern={patternUUID}
          // @ts-ignore determine props type shape
          {...props}
        />
      ),
    },
  };

  const handleSave = () => {
    if (modalMode === MODAL_MODES.ADD) {
      createOperator(customerId);
      setDisabledNextBtn(false);
      return;
    }
    editOperator(customerId);
    setDisabledNextBtn(false);
  };

  return (
    <OperatorFormWrapper>
      <FormattedMessage {...messages.operatorName} />
      <Input
        name="name"
        value={editableData.name}
        placeholder={intl.formatMessage(messages.operatorName)}
        onChange={e => {
          handleChangeForm({ name: e.target.value });
          setDisabledNextBtn(true);
        }}
        required={true}
      />
      <FormattedMessage {...messages.integratedEMRs} />
      <StyledDropdown menu={{ items }} trigger={['click']}>
        <PlusCircleTwoTone twoToneColor={'#87b7c5'} />
      </StyledDropdown>
      <StyledTable
        data={tableData}
        columns={columns}
        components={tableEditableComponents}
      />
      <ExtraActionBtn
        key="submit"
        onClick={handleSave}
        loading={isFormOperatorLoading}
        disabled={
          (editableData.emrMappings?.find(emr => emr.apiProviderId) &&
            !editableData.emrMappings?.find(
              emr => emr.organizationId.length,
            )) ||
          !editableData.name ||
          isFormOperatorLoading
        }
      >
        {modalMode && <FormattedMessage {...modesMessages[modalMode]} />}
      </ExtraActionBtn>
    </OperatorFormWrapper>
  );
};

const modesMessages = defineMessages({
  ADD: {
    id: 'OperatorsTab.Component.ADD',
    defaultMessage: 'Add Operator',
  },
  EDIT: {
    id: 'OperatorsTab.Component.EDIT',
    defaultMessage: 'Save changes',
  },
});

const messages = defineMessages({
  operatorName: {
    id: 'OperatorForm.index.operatorName',
    defaultMessage: 'Operator Name',
  },
  chooseOperatorDefaultUser: {
    id: 'OperatorForm.index.chooseOperatorDefaultUser',
    defaultMessage: "Choose Operator's default user",
  },
  chooseDefaultMtmTenant: {
    id: 'OperatorForm.index.chooseDefaultMtmTenant',
    defaultMessage: "Choose Operator's default MTM",
  },
  integratedEMRs: {
    id: 'OperatorForm.index.integratedEMRs',
    defaultMessage: 'Integrated EMRs',
  },
});

export default Connector(injectIntl(OperatorForm));
